import { useState } from 'react';
import { Search, Users2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useCustomers } from '@/api/hooks/useCustomers';
import type { Customer } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { CustomerHistoryDialog } from '@/features/customers/CustomerHistoryDialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDate, money } from '@/lib/utils';

export function CustomersPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [historyTarget, setHistoryTarget] = useState<Customer | null>(null);

  const { data, isLoading } = useCustomers(debouncedSearch, 1, 50);
  const customers = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-3xl">{t('customersTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          {data ? `${data.total} customer(s)` : 'Loading…'}
        </p>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or phone…"
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : customers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <Users2 className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {search
                ? 'No customers match that search.'
                : 'Customer records are created automatically when a phone number is entered during billing.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5">
          {customers.map((customer) => (
            <Card key={customer.id} className="flex items-center gap-3 p-3.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{customer.name || '—'}</p>
                <p className="text-xs text-muted-foreground">
                  {customer.phone}
                  {customer.lastVisit && ` · Last visit ${formatDate(customer.lastVisit)}`}
                </p>
              </div>
              <div className="hidden text-right sm:block">
                <p className="tabular text-sm font-semibold">{customer.visitCount} visit(s)</p>
              </div>
              <p className="tabular w-24 shrink-0 text-right text-sm font-semibold text-primary">
                {money(customer.totalSpend)}
              </p>
              <Button variant="outline" size="sm" onClick={() => setHistoryTarget(customer)}>
                History
              </Button>
            </Card>
          ))}
        </div>
      )}

      <CustomerHistoryDialog
        phone={historyTarget?.phone ?? null}
        name={historyTarget?.name || 'Customer'}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
      />
    </div>
  );
}
